import { LEGACY_PROMO_IMAGE_SOURCES } from './legacyPromoSources';
import { elakay_API } from '../../config';
import { isAuthenticated } from '../auth';
import { apiFetch, invalidatePublicDefaultPromo } from './apiDefaultPromo';
import { batchFiles, compressImageForUpload, uploadFeature, UPLOAD_LIMITS } from '../../utils/uploadPolicy';
import { messageForStatus } from '../../utils/uploadResponse';
const base = (scope) => `/swiper-image-files/${scope}`;
const changed = (promise) => promise.then((value) => { invalidatePublicDefaultPromo(); publicPool.clear(); return value; });
export const listImageFiles = (scope) => apiFetch(base(scope));
export const updateImageFile = (scope, id, body) => changed(apiFetch(`${base(scope)}/${id}`, { method: 'PATCH', body }));
export const deleteImageFile = (scope, id) => changed(apiFetch(`${base(scope)}/${id}`, { method: 'DELETE', body: { confirmed: true } }));
export const imageFileUsage = (scope, id) => apiFetch(`${base(scope)}/${id}/usage`);
export const reorderImageFiles = (scope, ids) => changed(apiFetch(`${base(scope)}/order`, { method: 'PUT', body: { ids } }));
export const saveImageFileSettings = (scope, body) => changed(apiFetch(`${base(scope)}/settings`, { method: 'PUT', body }));
const sendImageBatch = (scope, files, id, onProgress) => new Promise((resolve, reject) => {
  const form = new FormData();
  files.forEach((file) => form.append(id ? 'file' : 'files', file));
  const xhr = new XMLHttpRequest();
  xhr.open(id ? 'PUT' : 'POST', `${elakay_API}${base(scope)}${id ? `/${id}/file` : ''}`);
  const token = isAuthenticated()?.token;
  if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
  xhr.responseType = 'json';
  xhr.upload.onprogress = (event) => { if (event.lengthComputable && onProgress) onProgress(event.loaded / event.total); };
  xhr.onload = () => {
    if (xhr.status >= 200 && xhr.status < 300) resolve(xhr.response);
    // xhr.response is null for a proxy's HTML page (413): readable message.
    else reject(Object.assign(new Error(xhr.response?.error || messageForStatus(xhr.status, xhr.response)), { status: xhr.status, code: xhr.response?.code }));
  };
  xhr.onerror = () => reject(new Error("L'envoi des images a échoué. Vérifiez votre connexion."));
  xhr.send(form);
});

// Heavy photos are re-encoded first, then sent in batches whose total stays
// under the proxy ceiling (30 MiB per request, UP-01).
export const uploadImageFiles = (scope, files, id, progress) => changed((async () => {
  const prepared = await Promise.all(Array.from(files).map((file) => compressImageForUpload('swiperImageFile', file)));
  const batches = id ? [prepared.slice(0, 1)] : batchFiles(prepared, { maxBytes: UPLOAD_LIMITS.proxy.defaultRequestBytes - 1024 * 1024, maxFiles: uploadFeature('swiperImageFile').maxFiles });
  const total = prepared.reduce((sum, file) => sum + file.size, 0) || 1;
  let sent = 0;
  let merged = null;
  for (const batch of batches) {
    const size = batch.reduce((sum, file) => sum + file.size, 0);
    const result = await sendImageBatch(scope, batch, id, (ratio) => progress && progress(Math.round(((sent + ratio * size) / total) * 100)));
    sent += size;
    merged = merged ? { ...result, created: [...(merged.created || []), ...(result?.created || [])], errors: [...(merged.errors || []), ...(result?.errors || [])] } : result;
  }
  return merged;
})());
const publicPool = new Map();
export const getPublicImageFiles = (scope) => {
  const cached = publicPool.get(scope);
  if (cached && cached.expires > Date.now()) return cached.promise;
  const promise = Promise.resolve().then(() => apiFetch(`/swiper-image-files/public/${scope}`, { auth: false })).then((data) => {
    const present = new Set((data.images || []).map((image) => image.id));
    const bundled = (data.legacyPolicies || []).filter((entry) => entry.active && entry.selected && !present.has(entry.id)).map((entry) => {
      const source = LEGACY_PROMO_IMAGE_SOURCES.find((image) => image.legacyKey === entry.legacyKey);
      return source ? { id: entry.id, name: source.name, alt: source.name, url: source.url, thumbUrl: source.url } : null;
    }).filter(Boolean);
    return { ...data, images: scope === 'categories' && !data.settings?.enabled ? [] : [...(data.images || []), ...bundled] };
  }).catch(() => { publicPool.delete(scope); return { images: [], settings: {} }; });
  publicPool.set(scope, { promise, expires: Date.now() + 30000 });
  return promise;
};
